import { format } from 'date-fns';
import { TicketSearchRequest } from '../../types/interfaces/ticket';

export const TICKET_SEARCH_PAGE_SIZE = 50;

export type TicketSearchFormValue = Omit<
  TicketSearchRequest,
  'date' | 'endDate' | 'page' | 'limit'
> & {
  date?: Date | string | null;
  endDate?: Date | string | null;
};

export type NormalizedTicketSearchRequest = Partial<
  Omit<TicketSearchRequest, 'date' | 'endDate'>
> & {
  date?: string;
  endDate?: string;
};

export function buildTicketSearchRequest(
  request: TicketSearchFormValue,
  today = new Date(),
  page = 1,
): NormalizedTicketSearchRequest {
  const normalized: NormalizedTicketSearchRequest = {
    page,
    limit: TICKET_SEARCH_PAGE_SIZE,
  };
  const filter = request.filter?.trim();

  if (filter) {
    normalized.filter = filter;
  }

  if (request.queueIds?.length) {
    normalized.queueIds = request.queueIds;
  }

  if (request.statusIds?.length) {
    normalized.statusIds = request.statusIds;
  }

  const date = normalizeDate(request.date);
  const endDate = normalizeDate(request.endDate);

  if (date) {
    normalized.date = date;
  }

  if (endDate) {
    normalized.endDate = endDate;
  }

  if (!hasSearchConstraint(normalized)) {
    const currentDate = format(today, 'yyyy-MM-dd');
    normalized.date = currentDate;
    normalized.endDate = currentDate;
  }

  if (request.includeMessages) {
    normalized.includeMessages = true;
  }

  if (request.includeTickets) {
    normalized.includeTickets = true;
  }

  return normalized;
}

function normalizeDate(value?: Date | string | null): string | undefined {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return format(value, 'yyyy-MM-dd');
  }

  return typeof value === 'string' && value ? value : undefined;
}

function hasSearchConstraint(request: NormalizedTicketSearchRequest): boolean {
  return Boolean(
    request.filter ||
    request.queueIds?.length ||
    request.statusIds?.length ||
    request.date ||
    request.endDate,
  );
}
